import type {
  AssistantAnswerView,
  AssistantAskRequest,
  AssistantFeedbackRequest,
  AssistantHistoryTurn,
} from 'shared-models/browser';
import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';

const RATE_LIMITED_MESSAGE = 'הגעתם למכסת השאלות לשעה. נסו שוב בעוד כמה דקות.';
const UNAVAILABLE_MESSAGE = 'העוזר אינו זמין כרגע.';
const FORBIDDEN_MESSAGE = 'העוזר זמין למנהל משמרת בלבד.';

type StatusCarryingError = { response?: { status?: number } };

// Maps the transport failure to a user-facing Hebrew message; status wins over the server text.
const toPosAssistantErrorMessage = (error: unknown): string => {
  const status = (error as StatusCarryingError).response?.status;
  if (status === 429) return RATE_LIMITED_MESSAGE;
  if (status === 503) return UNAVAILABLE_MESSAGE;
  if (status === 403) return FORBIDDEN_MESSAGE;
  return extractServiceErrorMessage(error, 'POS assistant request failed');
};

class PosAssistantService {
  async ask(
    tenantId: string,
    employeeSession: string,
    question: string,
    history: AssistantHistoryTurn[] = [],
    signal?: AbortSignal,
  ): Promise<string> {
    const body: AssistantAskRequest = history.length ? { question, history } : { question };
    try {
      const response = await authenticatedInstance.post<AssistantAnswerView>(
        `/pos-assistant/tenant/${encodeURIComponent(tenantId)}/chat`,
        body,
        { signal, headers: { 'x-employee-session': employeeSession } },
      );
      return response.data.answer;
    } catch (error: unknown) {
      throw new Error(toPosAssistantErrorMessage(error));
    }
  }

  async sendFeedback(tenantId: string, employeeSession: string, message: string): Promise<void> {
    const body: AssistantFeedbackRequest = { message };
    try {
      await authenticatedInstance.post(
        `/pos-assistant/tenant/${encodeURIComponent(tenantId)}/feedback`,
        body,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(toPosAssistantErrorMessage(error));
    }
  }
}

export const posAssistantService = new PosAssistantService();
